import { bake_cookie, read_cookie, delete_cookie } from './cookies-service';
import { API } from './axios';
import { UserData } from './types';
import { DemoService } from './demo-service';
import { getDemoUserData } from './demo-data';

/**
 * `no_session` — no token, a rejected token, or an invalid user. Nothing the
 *   user can fix here; they have to come back through the main app.
 * `forbidden` — a real, valid Zooza user who lacks `manage_network`. Signing in
 *   again changes nothing, so this must never be presented as a login problem.
 */
export type SessionStatus = 'authenticated' | 'no_session' | 'forbidden';

export type SessionResult = {
    status: SessionStatus;
    user: UserData | null;
};

export class AuthService {
    private static instance: AuthService;
    private demoService: DemoService;

    public static getInstance(): AuthService {
        if (!AuthService.instance) {
            AuthService.instance = new AuthService();
        }
        return AuthService.instance;
    }

    private constructor() {
        this.demoService = DemoService.getInstance();
    }

    /**
     * Check URL parameters for token and company_id and save them to cookies
     */
    public handleUrlParams(): { token: string | null, company_id: string | null } {
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
        const company_id = urlParams.get('company_id');

        // Save token if present
        if (token) {
            bake_cookie('zooza-token', token);
            // Clean URL parameters after saving
            this.cleanUrlParams();
        }

        // Save company_id if present
        if (company_id) {
            bake_cookie('zooza-active-company', company_id);
        }

        return { token, company_id };
    }

    /**
     * Clean URL parameters after processing
     */
    private cleanUrlParams(): void {
        const url = new URL(window.location.href);
        url.searchParams.delete('token');
        url.searchParams.delete('company_id');
        window.history.replaceState({}, document.title, url.toString());
    }

    /**
     * Get authentication token from cookies or URL
     */
    public getToken(): string | null {
        // First check URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get('token');

        if (urlToken) {
            return urlToken;
        }

        // Then check cookies
        const cookieToken = read_cookie('zooza-token');
        return cookieToken || null;
    }

    /**
     * Get company ID from cookies or URL
     */
    public getCompanyId(): string | null {
        // First check URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const urlCompanyId = urlParams.get('company_id');

        if (urlCompanyId) {
            return urlCompanyId;
        }

        // Then check cookies
        const cookieCompanyId = read_cookie('zooza-active-company');
        return cookieCompanyId || null;
    }

    /**
     * Resolve the session: validate the token, check the network permission and
     * pick a company. One `/user` request, one answer — callers branch on
     * `status` instead of re-running this to find out what went wrong.
     */
    public async resolveSession(): Promise<SessionResult> {
        // Check if in demo mode
        if (this.demoService.isDemoMode()) {
            console.log('🎭 Demo mode active - returning mock user data');
            return {status: 'authenticated', user: getDemoUserData()};
        }

        // Handle URL parameters first
        this.handleUrlParams();

        const token = this.getToken();
        const company_id = this.getCompanyId();

        if (!token) {
            return {status: 'no_session', user: null};
        }

        // Set headers for API request
        API.defaults.headers.common['X-ZOOZA-TOKEN'] = token;
        if (company_id) {
            API.defaults.headers.common['X-ZOOZA-COMPANY'] = company_id;
        }

        try {
            const response = await API.get('/user');
            const userData: UserData = response.data;

            if (!userData.user_valid) {
                return {status: 'no_session', user: null};
            }

            // A valid user without `manage_network` is not a login problem —
            // keep the user around so the screen can address them by name.
            if (!this.hasManageNetworkPermission(userData)) {
                return {status: 'forbidden', user: userData};
            }

            // Handle company selection if no company_id was provided
            if (!company_id && userData.user.companies && userData.user.companies.length > 0) {
                const selectedCompany = userData.user.companies[0];
                bake_cookie('zooza-active-company', selectedCompany.id.toString());
                API.defaults.headers.common['X-ZOOZA-COMPANY'] = selectedCompany.id.toString();
            }

            return {status: 'authenticated', user: userData};
        } catch (error) {
            console.error('Session validation failed:', error);
            return {status: 'no_session', user: null};
        }
    }

    /**
     * Check if user has manage_network permission
     */
    private hasManageNetworkPermission(userData: UserData): boolean {
        if (!userData.user.permissions || !Array.isArray(userData.user.permissions)) {
            return false;
        }

        return userData.user.permissions.some(
            (permission) => permission.permission === 'manage_network' && permission.user_can
        );
    }

    /**
     * Clear authentication data
     */
    public clearAuth(): void {
        delete_cookie('zooza-token');
        delete_cookie('zooza-active-company');
        delete_cookie('zooza-network-id');
        delete API.defaults.headers.common['X-ZOOZA-TOKEN'];
        delete API.defaults.headers.common['X-ZOOZA-COMPANY'];
    }

    /**
     * Check if user is authenticated
     */
    public isAuthenticated(): boolean {
        // In demo mode, always consider authenticated
        if (this.demoService.isDemoMode()) {
            return true;
        }
        return !!this.getToken();
    }
}
